import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  RemoveCircle as InactiveIcon,
  Build as MaintenanceIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import integrationService from '../../services/integrationService'
import type { HealthStatus, ServiceStatus } from '../../types/integration'

interface HealthStatusCardProps {
  integration: HealthStatus
  integrationId?: string
  onCheckComplete?: () => void
}

const HealthStatusCard: React.FC<HealthStatusCardProps> = ({
  integration,
  integrationId,
  onCheckComplete
}) => {
  const queryClient = useQueryClient()
  const [showDetails, setShowDetails] = useState(false)

  // Manual health check mutation
  const checkHealthMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) {
        throw new Error('Integration ID is required for health check')
      }
      return integrationService.checkIntegrationHealth(integrationId)
    },
    onSuccess: () => {
      // Invalidate health data to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['integration-health'] })
      if (onCheckComplete) {
        onCheckComplete()
      }
    }
  })

  // Get status color
  const getStatusColor = (status: ServiceStatus): 'success' | 'error' | 'default' | 'warning' => {
    switch (status) {
      case 'active':
        return 'success'
      case 'error':
        return 'error'
      case 'inactive':
        return 'default'
      case 'maintenance':
        return 'warning'
      default:
        return 'default'
    }
  }

  // Get status icon
  const getStatusIcon = (status: ServiceStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" fontSize="large" />
      case 'error':
        return <ErrorIcon color="error" fontSize="large" />
      case 'inactive':
        return <InactiveIcon color="disabled" fontSize="large" />
      case 'maintenance':
        return <MaintenanceIcon color="warning" fontSize="large" />
      default:
        return <InactiveIcon color="disabled" fontSize="large" />
    }
  }

  // Get response time color based on performance
  const getResponseTimeColor = (responseTime?: number): string => {
    if (!responseTime) return 'text.secondary'
    if (responseTime < 500) return 'success.main'
    if (responseTime < 2000) return 'warning.main'
    return 'error.main'
  }

  // Calculate response time bar percentage (max 5000ms = 100%)
  const getResponseTimePercentage = (responseTime?: number): number => {
    if (!responseTime) return 0
    return Math.min((responseTime / 5000) * 100, 100)
  }

  // Format timestamp
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return date.toLocaleString()
  }

  const handleCheckNow = () => {
    checkHealthMutation.mutate()
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header: Service name and status icon */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box flex={1}>
            <Typography variant="h6" component="div" gutterBottom>
              {integration.provider_name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {integration.service_type.toUpperCase()}
            </Typography>
          </Box>
          <Box>{getStatusIcon(integration.status)}</Box>
        </Box>

        {/* Status badge */}
        <Box mb={2}>
          <Chip
            label={integration.status.toUpperCase()}
            color={getStatusColor(integration.status)}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        {/* Response time with visual indicator */}
        {integration.response_time_ms !== undefined && integration.status === 'active' && (
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2" color="textSecondary">
                Response Time
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={getResponseTimeColor(integration.response_time_ms)}
              >
                {integration.response_time_ms}ms
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getResponseTimePercentage(integration.response_time_ms)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getResponseTimeColor(integration.response_time_ms),
                  borderRadius: 3
                }
              }}
            />
            <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
              {integration.response_time_ms < 500
                ? 'Excellent'
                : integration.response_time_ms < 2000
                ? 'Good'
                : 'Slow'}
            </Typography>
          </Box>
        )}

        {/* Uptime percentage */}
        {integration.uptime_percentage !== undefined && (
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Uptime
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <LinearProgress
                variant="determinate"
                value={integration.uptime_percentage}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor:
                      integration.uptime_percentage >= 99
                        ? 'success.main'
                        : integration.uptime_percentage >= 95
                        ? 'warning.main'
                        : 'error.main',
                    borderRadius: 3
                  }
                }}
              />
              <Typography variant="body2" fontWeight="bold">
                {integration.uptime_percentage.toFixed(2)}%
              </Typography>
            </Box>
          </Box>
        )}

        {/* Last check timestamp */}
        <Box mb={2}>
          <Tooltip title={new Date(integration.last_check).toLocaleString()}>
            <Typography variant="caption" color="textSecondary" display="block">
              Last checked: {formatTimestamp(integration.last_check)}
            </Typography>
          </Tooltip>
        </Box>

        {/* Error message */}
        {integration.error_message && (
          <Alert
            severity="error"
            sx={{ mb: 2, cursor: 'pointer' }}
            onClick={() => setShowDetails(!showDetails)}
          >
            <Typography variant="caption">
              {showDetails
                ? integration.error_message
                : integration.error_message.length > 60
                ? `${integration.error_message.substring(0, 60)}...`
                : integration.error_message}
            </Typography>
            {integration.error_message.length > 60 && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                {showDetails ? 'Click to collapse' : 'Click to see full error'}
              </Typography>
            )}
          </Alert>
        )}

        {/* Spacer to push button to bottom */}
        <Box flexGrow={1} />

        {/* Check Now button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={
            checkHealthMutation.isPending ? (
              <CircularProgress size={16} />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={handleCheckNow}
          disabled={checkHealthMutation.isPending || !integrationId}
          fullWidth
          sx={{ mt: 'auto' }}
        >
          {checkHealthMutation.isPending ? 'Checking...' : 'Check Now'}
        </Button>

        {/* Check error */}
        {checkHealthMutation.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <Typography variant="caption">
              Failed to check health. Please try again.
            </Typography>
          </Alert>
        )}

        {/* Check success */}
        {checkHealthMutation.isSuccess && (
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography variant="caption">Health check completed successfully</Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default HealthStatusCard
